import { createDateFromString } from 'utils/date'
import { isAfter, isBefore, startOfDay } from 'date-fns'

/**
 * Determine whether a given day falls within a vacation hold range.
 *
 * The function normalizes all dates to the start of their day (local timezone)
 * to perform day-accurate comparisons. Behavior:
 * - If `endDate` is provided:
 *   - If `startDate` and `endDate` are the same day, the hold is active only on that day.
 *   - Otherwise the range is treated as inclusive: active when startDate <= targetDate <= endDate.
 * - If `endDate` is omitted or null, the hold is active on or after `startDate` (inclusive).
 *
 * Note: input values are converted using `createDateFromString(...)` and the function
 * uses `startOfDay(...)` to normalize dates in the runtime's local timezone.
 *
 * @param {string|Date|number} startDate - The start of the vacation hold. A date-like value
 *   accepted by `createDateFromString` (e.g. ISO date string, Date object, or timestamp).
 * @param {string|Date|number|null|undefined} [endDate] - Optional end of the vacation hold.
 *   If omitted/null, the hold is considered open-ended and active on/after `startDate`.
 * @param {string|Date|number} [targetDate=new Date()] - Optional date to check against the range.
 *   Defaults to the current date if omitted.
 * @returns {boolean} True if the target day is within the vacation hold range, false otherwise.
 *
 * @throws {Error} If `startDate` (or `endDate`/`targetDate`, when provided) cannot be parsed.
 *
 * @example
 * // Active during the inclusive range (checks today by default)
 * isVacationHoldActiveWithinRange('2025-10-01', '2025-10-10') // => true/false depending on today
 *
 * @example
 * // Open-ended hold: active on or after 2025-10-01
 * isVacationHoldActiveWithinRange('2025-10-01') // => true if today >= 2025-10-01
 *
 * @example
 * // Check a specific date against the range
 * isVacationHoldActiveWithinRange('2025-10-01', '2025-10-10', '2025-10-05') // => true
 */
export const isVacationHoldActiveWithinRange = (startDate, endDate, targetDate = null) => {
  let dateToCheck = new Date()
  if (targetDate) {
    dateToCheck = typeof targetDate === 'string' ? createDateFromString(targetDate) : targetDate
  }

  const startDateObj = createDateFromString(startDate)
  const endDateObj = endDate ? createDateFromString(endDate) : null

  // Normalize to start of day for accurate day-based comparisons
  const targetDay = startOfDay(dateToCheck)
  const startDay = startOfDay(startDateObj)
  const endDay = endDateObj ? startOfDay(endDateObj) : null

  if (endDay) {
    // Inclusive range: targetDay >= startDay && targetDay <= endDay
    const onOrAfterStart = !isBefore(targetDay, startDay)
    const onOrBeforeEnd = !isAfter(targetDay, endDay)
    return onOrAfterStart && onOrBeforeEnd
  }

  // No end date: active on or after the start date
  return !isBefore(targetDay, startDay)
}
